import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import {
  listMyAvailability, saveAvailabilitySlot, deleteAvailabilitySlot,
  saveAvailabilityBlock, deleteAvailabilityBlock, listMyUpcomingShifts,
} from "@/lib/availability.functions";
import { ReserveHeader } from "@/components/ReserveHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export const Route = createFileRoute("/_authenticated/bartender/availability")({
  head: () => ({ meta: [{ title: "My availability — The Hospitality Reserve" }] }),
  component: AvailabilityPage,
  errorComponent: ({ error }) => <div className="p-8 text-sm text-destructive">{error.message}</div>,
  notFoundComponent: () => <div className="p-8">Not found.</div>,
});

const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

type Slot = { id: string; day_of_week: number; start_time: string; end_time: string; notes: string | null };
type Block = { id: string; starts_at: string; ends_at: string; reason: string | null };
type UpcomingShift = { id: string; shift_id: string; shifts: { start_time: string; end_time: string; role_type: string; address: string | null; postcode: string | null; hourly_rate: number } | null };

function AvailabilityPage() {
  const loadAvail = useServerFn(listMyAvailability);
  const loadShifts = useServerFn(listMyUpcomingShifts);
  const addSlot = useServerFn(saveAvailabilitySlot);
  const delSlot = useServerFn(deleteAvailabilitySlot);
  const addBlock = useServerFn(saveAvailabilityBlock);
  const delBlock = useServerFn(deleteAvailabilityBlock);

  const [slots, setSlots] = useState<Slot[]>([]);
  const [blocks, setBlocks] = useState<Block[]>([]);
  const [shifts, setShifts] = useState<UpcomingShift[]>([]);
  const [loading, setLoading] = useState(true);

  const [day, setDay] = useState(1);
  const [start, setStart] = useState("18:00");
  const [end, setEnd] = useState("23:00");

  const [blkStart, setBlkStart] = useState("");
  const [blkEnd, setBlkEnd] = useState("");
  const [blkReason, setBlkReason] = useState("");

  const refresh = async () => {
    setLoading(true);
    const [a, s] = await Promise.all([loadAvail(), loadShifts()]);
    if ("error" in a) toast.error(a.error); else { setSlots(a.slots as Slot[]); setBlocks(a.blocks as Block[]); }
    if ("error" in s) toast.error(s.error); else setShifts(s.shifts as UpcomingShift[]);
    setLoading(false);
  };
  useEffect(() => { refresh(); }, []);

  const handleAddSlot = async () => {
    try {
      const r = await addSlot({ data: { day_of_week: day, start_time: start, end_time: end } });
      if ("error" in r) throw new Error(r.error);
      toast.success("Saved."); await refresh();
    } catch (e) { toast.error(e instanceof Error ? e.message : "Could not save."); }
  };
  const handleAddBlock = async () => {
    if (!blkStart || !blkEnd) return;
    try {
      const r = await addBlock({ data: { starts_at: new Date(blkStart).toISOString(), ends_at: new Date(blkEnd).toISOString(), reason: blkReason || null } });
      if ("error" in r) throw new Error(r.error);
      toast.success("Block saved."); setBlkStart(""); setBlkEnd(""); setBlkReason(""); await refresh();
    } catch (e) { toast.error(e instanceof Error ? e.message : "Could not save."); }
  };

  return (
    <div className="min-h-screen bg-background">
      <ReserveHeader />
      <main className="mx-auto max-w-3xl px-6 py-10">
        <p className="text-[11px] tracking-wide-luxury text-muted-foreground">Bartender</p>
        <h1 className="text-3xl font-light">Availability & calendar</h1>
        <p className="mt-3 max-w-xl text-sm text-muted-foreground">Set the days and times you're typically free, and block out holiday or sickness windows.</p>

        <section className="mt-6 border border-border p-6">
          <h2 className="text-lg font-light">Weekly availability</h2>
          <div className="mt-4 grid gap-3 sm:grid-cols-[auto_1fr_1fr_auto] sm:items-end">
            <div>
              <Label>Day</Label>
              <select className="block h-10 rounded-md border border-input bg-background px-3 text-sm" value={day} onChange={(e) => setDay(Number(e.target.value))}>
                {DAYS.map((d, i) => <option key={d} value={i}>{d}</option>)}
              </select>
            </div>
            <div><Label>Start</Label><Input type="time" value={start} onChange={(e) => setStart(e.target.value)} /></div>
            <div><Label>End</Label><Input type="time" value={end} onChange={(e) => setEnd(e.target.value)} /></div>
            <Button onClick={handleAddSlot}>Add slot</Button>
          </div>
          {loading ? <p className="mt-4 text-sm text-muted-foreground">Loading…</p>
          : slots.length === 0 ? <p className="mt-4 text-sm text-muted-foreground">No availability set.</p>
          : (
            <ul className="mt-4 divide-y divide-border border-t border-border">
              {slots.map((s) => (
                <li key={s.id} className="flex items-center justify-between py-3 text-sm">
                  <span>{DAYS[s.day_of_week]} · {s.start_time.slice(0,5)}–{s.end_time.slice(0,5)}</span>
                  <button type="button" onClick={async () => { const r = await delSlot({ data: { id: s.id } }); if ("error" in r) toast.error(r.error); else { toast.success("Removed."); refresh(); } }} className="text-xs text-destructive hover:underline">Remove</button>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="mt-6 border border-border p-6">
          <h2 className="text-lg font-light">Unavailable dates</h2>
          <div className="mt-4 grid gap-3 sm:grid-cols-[1fr_1fr_1fr_auto] sm:items-end">
            <div><Label>From</Label><Input type="datetime-local" value={blkStart} onChange={(e) => setBlkStart(e.target.value)} /></div>
            <div><Label>To</Label><Input type="datetime-local" value={blkEnd} onChange={(e) => setBlkEnd(e.target.value)} /></div>
            <div><Label>Reason (optional)</Label><Input value={blkReason} onChange={(e) => setBlkReason(e.target.value)} placeholder="Holiday" /></div>
            <Button onClick={handleAddBlock} disabled={!blkStart || !blkEnd}>Add block</Button>
          </div>
          {blocks.length === 0 ? <p className="mt-4 text-sm text-muted-foreground">No upcoming blocks.</p>
          : (
            <ul className="mt-4 divide-y divide-border border-t border-border">
              {blocks.map((b) => (
                <li key={b.id} className="flex items-center justify-between py-3 text-sm">
                  <span>{new Date(b.starts_at).toLocaleString("en-GB")} → {new Date(b.ends_at).toLocaleString("en-GB")}{b.reason ? ` · ${b.reason}` : ""}</span>
                  <button type="button" onClick={async () => { const r = await delBlock({ data: { id: b.id } }); if ("error" in r) toast.error(r.error); else { toast.success("Removed."); refresh(); } }} className="text-xs text-destructive hover:underline">Remove</button>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="mt-6 border border-border p-6">
          <h2 className="text-lg font-light">Upcoming confirmed shifts</h2>
          {shifts.length === 0 ? <p className="mt-4 text-sm text-muted-foreground">No upcoming shifts.</p>
          : (
            <ul className="mt-4 divide-y divide-border border-t border-border">
              {shifts.map((s) => s.shifts && (
                <li key={s.id} className="py-3 text-sm">
                  <p>{new Date(s.shifts.start_time).toLocaleString("en-GB")} – {new Date(s.shifts.end_time).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}</p>
                  <p className="text-xs text-muted-foreground">{s.shifts.role_type} · {s.shifts.address ?? "—"} {s.shifts.postcode ?? ""} · £{s.shifts.hourly_rate}/hr</p>
                </li>
              ))}
            </ul>
          )}
        </section>
      </main>
    </div>
  );
}
